import { useRoute, useRouter } from 'vue-router'
import type { LocationQuery } from 'vue-router'
import Network from '@/Network'
import {
  ActivityType,
  ContainerKind,
  defaultContainerOrderBy,
  DSAttendanceStatus
} from '@/constants/ds-attendance.constants'
import type {
  DSAttendanceContainerOrderBy,
  ListContainersByCompanyParams
} from '@/types/ds-attendance.types'
import { routeNameForStatus } from '../utils/status-route-map'
import { readShowNoScheduleExpected } from '../utils/no-schedule-expected-preference'
import { containerQueueParamsFromQuery } from './useDsAttendanceQueueFilters'

/**
 * Eixo 4 da discovery 2026-09-11 — "Aprovar e abrir o próximo".
 *
 * Cinco pessoas, ~16 aprovações por dia, p90 de 5,8 dias: o que envelhece é o que ninguém
 * pega, e boa parte do tempo entre dois registros é a ida e volta à lista. Este composable é a
 * máquina por baixo do botão: a fila que o auditor estava olhando viaja na query da rota do
 * registro, e o "próximo" é pedido ao backend com exatamente esses filtros.
 *
 * Consumidores: `DsAttendanceAwaitingShipment.vue` ("Próximo registro ›") e
 * `DsAttendanceAwaitingAudit.vue` ("Aprovar e abrir o próximo" + `Ctrl+Enter`).
 */

/** Aceita o valor cru da query (string, array ou ausente) e devolve sempre um array. */
function queryToArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap((item) => queryToArray(item))
  if (value === null || value === undefined || value === '') return []
  return String(value).split(',').filter(Boolean)
}

function queryToString(value: unknown): string | null {
  if (Array.isArray(value)) return queryToString(value[0])
  return value === null || value === undefined || value === '' ? null : String(value)
}

function queryToNumbers(value: unknown): number[] {
  return queryToArray(value)
    .map(Number)
    .filter((n) => Number.isFinite(n))
}

/**
 * Query da lista (o que `Index.vue` escreve na URL) → params do `by-company`. É a mesma
 * tradução que a lista faz ao montar `containerFilters`; mantida aqui para o registro não
 * precisar da store da lista, que pode já ter sido recarregada com outros filtros.
 */
export function containerFiltersFromQuery(query: LocationQuery): ListContainersByCompanyParams {
  const validStatus = Object.values(DSAttendanceStatus) as string[]
  const status = queryToArray(query.status).filter((s) =>
    validStatus.includes(s)
  ) as DSAttendanceStatus[]

  // F2 — `Index.vue` escreve `kind`/`activityType` na URL quando o auditor filtra pela aba
  // Atividade/Solicitações; sem repassar aqui, o "próximo" ignorava a aba e podia devolver um
  // container de espécie diferente da que a tela está mostrando. A lista só aplica UM `kind`
  // por vez ao backend (`setKindFiltersFromUrl`), por isso pegamos só o primeiro do CSV.
  const validKinds = Object.values(ContainerKind) as string[]
  const kindCandidate = queryToArray(query.kind)[0]
  const kind =
    kindCandidate && validKinds.includes(kindCandidate)
      ? (kindCandidate as ContainerKind)
      : undefined

  const validActivityTypes = Object.values(ActivityType) as string[]
  const activityTypeCandidate = queryToString(query.activityType)
  const activityType =
    activityTypeCandidate && validActivityTypes.includes(activityTypeCandidate)
      ? (activityTypeCandidate as ActivityType)
      : undefined

  return {
    kind,
    activityType,
    // `page`/`limit` não fazem sentido para "o próximo": quem pagina é a lista, e o backend
    // resolve o próximo sobre a fila inteira ordenada.
    page: 1,
    limit: 1,
    status,
    healthUnitIds: queryToNumbers(query.healthUnitIds),
    scheduleIds: queryToNumbers(query.scheduleIds),
    shiftIds: queryToNumbers(query.shiftIds),
    startDate: queryToString(query.startDate),
    endDate: queryToString(query.endDate),
    uploadStartDate: queryToString(query.uploadStartDate),
    uploadEndDate: queryToString(query.uploadEndDate),
    hasDivergentEntries: queryToString(query.hasDivergentEntries) === 'true',
    hasAntecipe: queryToString(query.hasAntecipe) === 'true',
    hasInCash: queryToString(query.hasInCash) === 'true',
    stationChangedAfterAudit: queryToString(query.stationChangedAfterAudit) === 'true',
    manualReviewReason: queryToArray(query.manualReviewReason),
    /**
     * A lista só escreve `sort` na URL quando ele difere do padrão — sem o `??` o próximo vinha
     * do `referenceDate ASC` do backend, ou seja, de uma fila diferente da que está na tela.
     * O padrão é o da lista: `SLA` fora da aba Solicitações (spec 2026-10-01, F17); na aba
     * Solicitações continua `PAYMENT_PRIORITY` (CA9).
     */
    orderBy:
      (queryToString(query.sort) as DSAttendanceContainerOrderBy | null) ??
      defaultContainerOrderBy(queryToString(query.onlyRequests) === 'true'),
    /**
     * "Mostrar sem escala prevista" é preferência de usuário no `localStorage` e NÃO viaja na
     * URL: quem quer o próximo tem de ler a mesma preferência, senão o backend esconde
     * registros que estão visíveis na fila e pula justamente o próximo da lista.
     */
    includeNoScheduleExpected: readShowNoScheduleExpected(),
    // Spec 2026-10-01 B7 — o recorte SLA da lista (etapas, estado do prazo, só vencidos,
    // sem solicitações) viaja junto; sem Etapa, só etapas abertas.
    ...containerQueueParamsFromQuery(query as Record<string, unknown>)
  }
}

export function useNextContainer() {
  const route = useRoute()
  const router = useRouter()

  /**
   * Registro de ORIGEM do último `fetchNextId` desta instância do composable.
   *
   * O "Aprovar e abrir o próximo" navega para a MESMA rota (`DsAttendancePendingAudit` →
   * `DsAttendancePendingAudit`), então o componente é reaproveitado e um `knownNextId`
   * pré-buscado para o registro ANTERIOR sobrevive à navegação. Sem amarrar o id pré-buscado
   * ao registro que o originou, a segunda aprovação da sequência reusaria o id da primeira e
   * levaria o auditor para um registro que não é o próximo da fila.
   */
  let prefetchedFor: string | null = null

  /** A query com que a lista navegou até aqui — é ela que define "a fila atual". */
  function queueQueryFromRoute(): LocationQuery {
    return { ...route.query }
  }

  /**
   * `null` significa "não há próximo" e NÃO é erro: fila acabou, ou backend ainda sem a rota
   * (o web deploya antes). Por isso a falha é silenciosa — um toast vermelho a cada aprovação
   * num ambiente sem a rota seria pior que a ausência do botão.
   */
  async function fetchNextId(currentId: number | string): Promise<number | null> {
    try {
      const params = containerFiltersFromQuery(queueQueryFromRoute())
      const nextId = await Network.dsAttendance.getNextContainerId(currentId, params)
      prefetchedFor = String(currentId)
      return nextId
    } catch (error) {
      console.debug('[useNextContainer] fila sem próximo registro ou rota indisponível:', error)
      prefetchedFor = String(currentId)
      return null
    }
  }

  /**
   * Navega para o próximo registro carregando a MESMA query — assim o encadeamento continua
   * a partir dele. Devolve `false` quando não havia próximo (o chamador volta para a lista).
   *
   * `knownNextId` é o id já pré-buscado na abertura da tela e tem PREFERÊNCIA: depois de
   * aprovar, o registro atual saiu da fila, e consultar de novo devolveria o primeiro id da
   * fila — não o que estava logo depois deste. Só sem id conhecido a consulta acontece.
   *
   * A preferência vale apenas enquanto o id pré-buscado for DESTE registro: se ele foi
   * resolvido enquanto a tela mostrava outro container, é lixo de navegação e a consulta
   * acontece na hora.
   */
  async function goToNext(
    currentId: number | string,
    knownNextId?: number | null
  ): Promise<boolean> {
    const isPrefetchFromThisContainer = prefetchedFor === String(currentId)
    const nextId =
      isPrefetchFromThisContainer && knownNextId !== null && knownNextId !== undefined
        ? knownNextId
        : await fetchNextId(currentId)
    if (nextId === null) return false

    const details = await Network.dsAttendance.getById(nextId)
    const routeName = routeNameForStatus(details?.status ?? null)
    if (!routeName) return false

    await router.push({
      name: routeName,
      params: { attendanceId: String(nextId) },
      query: queueQueryFromRoute(),
      state: { details: JSON.parse(JSON.stringify(details)), status: details.status }
    })
    return true
  }

  return { queueQueryFromRoute, fetchNextId, goToNext }
}
